import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { AuthPanel } from '../pages/auth/AuthPanel'
import { platformBrandVars } from '../components/layout/useVertical'
import { AuthModalContext } from './authModalContext'
import { onAuthModalRequest } from '../lib/authModalBridge'
import { useAuth } from './useAuth'

/**
 * Sign-in as a popup over the current page.
 *
 * Same `AuthPanel` the `/login` route renders — identical UI and flows, only the
 * chrome differs. The point is context: a visitor who hits a gated action used to
 * be navigated away to `/login`, losing scroll position, filters, and the item
 * they were looking at. The modal leaves the page underneath intact.
 *
 * `/login` remains a real route for the entries a modal cannot serve — session
 * expiry (a hard redirect from outside React), the catch-all route, and the
 * return leg of password reset.
 */

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const { logout } = useAuth()

  const openAuthModal = useCallback(() => setOpen(true), [])
  const closeAuthModal = useCallback(() => setOpen(false), [])

  // An expired session reaches us from `api/client.ts`, outside React. Clear the
  // stale credentials before prompting — `logout` is best-effort, so the doomed
  // revoke call against an already-dead token is caught and ignored.
  useEffect(() => {
    return onAuthModalRequest(() => {
      void logout()
      setOpen(true)
    })
  }, [logout])

  // Escape closes, and the page behind must not scroll while the dialog is up.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const value = useMemo(
    () => ({ openAuthModal, closeAuthModal, isAuthModalOpen: open }),
    [openAuthModal, closeAuthModal, open],
  )

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Sign in to OSLE"
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-stone-950/50 p-4 py-8 sm:items-center"
        >
          {/* Backdrop click closes. A sibling button rather than a handler on the
              overlay, so clicks inside the panel never bubble out to it. */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={closeAuthModal}
            className="fixed inset-0 -z-10 cursor-default"
          />
          {/* platformBrandVars: AuthPanel also pins emerald; wrap again so the modal
              shell cannot inherit a future parent theme wrapper. */}
          <div className="w-full max-w-5xl" style={platformBrandVars()}>
            <AuthPanel onClose={closeAuthModal} />
          </div>
        </div>
      )}
    </AuthModalContext.Provider>
  )
}
